import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type { FounderUpdateExtraction, FounderUpdateInboxItem, FounderUpdateInboxItemStatus } from '../types';
import { db } from '../lib/firebase';
import { COLLECTION_FOUNDER_UPDATE_INBOX_ITEMS } from '../lib/firestorePaths';

const INBOX_STATUSES: FounderUpdateInboxItemStatus[] = [
  'unmatched',
  'matched',
  'processed',
  'dismissed',
];

export type FounderUpdateInboxState = {
  items: FounderUpdateInboxItem[];
  unmatchedItems: FounderUpdateInboxItem[];
  loading: boolean;
  error: string | null;
};

const EMPTY_STATE: FounderUpdateInboxState = {
  items: [],
  unmatchedItems: [],
  loading: false,
  error: null,
};

function docItemId(d: QueryDocumentSnapshot, data: { id?: unknown }): string {
  if (typeof data.id === 'string' && data.id.trim()) return data.id.trim();
  const parts = d.id.split('__');
  return parts.length > 1 ? parts.slice(1).join('__') : d.id;
}

function asExtraction(value: unknown): FounderUpdateExtraction | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const x = value as FounderUpdateExtraction;
  if (typeof x.summary !== 'string') return undefined;
  return x;
}

function docToFounderUpdateInboxItem(d: QueryDocumentSnapshot): FounderUpdateInboxItem {
  const x = d.data() as Record<string, unknown>;
  const now = new Date().toISOString();
  const id = docItemId(d, x);
  const status = INBOX_STATUSES.includes(x.status as FounderUpdateInboxItemStatus)
    ? (x.status as FounderUpdateInboxItemStatus)
    : 'unmatched';

  return {
    id,
    userId: typeof x.userId === 'string' ? x.userId : '',
    status,
    companyId: typeof x.companyId === 'string' ? x.companyId : undefined,
    sourceMessageId: typeof x.sourceMessageId === 'string' ? x.sourceMessageId : id,
    fromEmail: typeof x.fromEmail === 'string' ? x.fromEmail : '',
    subject: typeof x.subject === 'string' ? x.subject : undefined,
    receivedAt: typeof x.receivedAt === 'string' ? x.receivedAt : now,
    rawText: typeof x.rawText === 'string' ? x.rawText : '',
    extraction: asExtraction(x.extraction),
    createdUpdateId: typeof x.createdUpdateId === 'string' ? x.createdUpdateId : undefined,
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : now,
    updatedAt: typeof x.updatedAt === 'string' ? x.updatedAt : now,
  };
}

function sortInboxItems(items: FounderUpdateInboxItem[]): FounderUpdateInboxItem[] {
  return [...items].sort(
    (a, b) => new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime()
  );
}

export function useFounderUpdateInboxItems(userId: string): FounderUpdateInboxState {
  const [items, setItems] = useState<FounderUpdateInboxItem[]>([]);
  const [loading, setLoading] = useState(Boolean(userId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setItems([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    const qInbox = query(
      collection(db, COLLECTION_FOUNDER_UPDATE_INBOX_ITEMS),
      where('userId', '==', userId)
    );

    const applyItems = (docs: QueryDocumentSnapshot[]) => {
      setItems(sortInboxItems(docs.map(docToFounderUpdateInboxItem)));
    };

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const snap = await getDocs(qInbox);
        if (cancelled) return;

        applyItems(snap.docs);

        unsubscribe = onSnapshot(
          qInbox,
          (next) => applyItems(next.docs),
          (e) => setError(e.message)
        );
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load founder update inbox');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [userId]);

  return useMemo(() => {
    const unmatchedItems = items.filter((item) => item.status === 'unmatched');
    return {
      items,
      unmatchedItems,
      loading,
      error,
    };
  }, [items, loading, error]);
}

export function emptyFounderUpdateInboxState(): FounderUpdateInboxState {
  return EMPTY_STATE;
}
